import React, { useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import { SectionContext } from '../../../../contexts/section-context'
import { useCommonServices } from '../../../../lib/hooks'
import { Typography } from '@grammarly/badger-ui'
import {
  getInstallDisplayName,
  installPageLinks,
  InstallType,
  useFirstEligibleInstall,
} from '../../../../lib/hooks/install-eligibility'
import { InternalLink } from '../../../ui-lib/link'
import { ButtonCtaProps } from '../../../ui-lib/button'
import { CtaTrackingProps } from '../types'
import { NotAvailableButton } from './not-available-button'
import styles from './unsupported.module.scss'

const { C4 } = Typography

interface AlternateLinkProps {
  preferredInstall?: InstallType
}

const AlternateLink: React.FC<AlternateLinkProps> = ({ preferredInstall }) => {
  const { browserService } = useCommonServices()

  if (preferredInstall) {
    const installDisplayName = getInstallDisplayName(preferredInstall, browserService)
    const href = installPageLinks[preferredInstall]
    return (
      <InternalLink href={href}>
        <FormattedMessage
          id="ctaButton.unsupported.alternateLink.ctaText"
          description="CTA Button - Unsupported - Alternate Link - CTA Text"
          defaultMessage="Get Grammarly for {installDisplayName}"
          values={{ installDisplayName }}
        />
      </InternalLink>
    )
  }

  return null
}

interface NotAvailableOnYourDeviceTextProps extends AlternateLinkProps {
  unavailableText?: string
}

const NotAvailableOnYourDeviceText: React.FC<NotAvailableOnYourDeviceTextProps> = ({
  preferredInstall,
  unavailableText = 'This version of Grammarly is not available on your current device.',
}) => {
  return (
    <div className={styles.notAvailableText}>
      <C4 as="div">{unavailableText}</C4>
      <C4 as="div">
        <AlternateLink preferredInstall={preferredInstall} />
      </C4>
    </div>
  )
}

interface UnsupportedButtonProps
  extends Omit<ButtonCtaProps, 'text'>,
    Pick<NotAvailableOnYourDeviceTextProps, 'unavailableText'>,
    CtaTrackingProps {
  alternateLinkInstallTypes: InstallType[]
}

export const UnsupportedButton: React.FC<UnsupportedButtonProps> = ({
  alternateLinkInstallTypes,
  unavailableText,
  placement,
  placementInternalName,
  ...rest
}) => {
  const { isHero } = useContext(SectionContext)
  const preferredInstall = useFirstEligibleInstall(alternateLinkInstallTypes)
  return (
    <>
      <NotAvailableButton {...rest} />
      {isHero && <NotAvailableOnYourDeviceText preferredInstall={preferredInstall} unavailableText={unavailableText} />}
    </>
  )
}
